import { ReactNode, useCallback, useEffect, useRef } from 'react';
import {
    useNavigate as _useNavigate,
    createPath,
    NavigateOptions,
    NavigateProps,
    parsePath,
    Path,
    To
} from 'react-router-dom';
import Dialog from 'components/base/Dialog';
import URL from 'utils/URL';

interface PowerExternalOptions {
    sync?: boolean; // 同步当前路由的所有search参数
    next?: string; // 指定要带上的next参数，优先级高于sync
    confirmMessage?: ReactNode | (() => Promise<void>); // 跳转前的消息提示，或者传递一个promise回调，自定义提示
}

export type PowerNavigateOptions = NavigateOptions & PowerExternalOptions;
export type PowerNavigateProps = NavigateProps & PowerExternalOptions;

export function usePowerNavigate() {
    const routeNavigate = _useNavigate();
    const confirmResolveRef = useRef<Promise<void> | null>(null);
    const navigate = useCallback(
        (to: To | number, options?: PowerNavigateOptions) => {
            const applyNavigate = () => {
                if (typeof to === 'number') {
                    return routeNavigate(to);
                }

                // 同时直接页面跳转和路由切换跳转
                const _navigate = (to: Partial<Path>, options?: NavigateOptions) => {
                    if (URL.parse(to.pathname || '').host === window.location.host) {
                        const replace = options?.replace;

                        if (replace) {
                            window.location.replace(createPath(to));
                        } else {
                            window.location.href = createPath(to);
                        }
                    } else {
                        routeNavigate(to, options);
                    }
                };

                const toPath = typeof to === 'string' ? parsePath(to) : to;

                if (options) {
                    const toSearchParams = new URLSearchParams(toPath.search);

                    if (options.sync) {
                        const searchParams = new URLSearchParams(window.location.search);

                        searchParams.forEach((value, key) => {
                            toSearchParams.set(key, value);
                        });
                    }

                    if (options.next) {
                        toSearchParams.set('next', options.next);
                    }

                    toPath.search = toSearchParams.toString();

                    return _navigate(toPath, options);
                }

                return _navigate(toPath);
            };

            if (options?.confirmMessage) {
                if (!confirmResolveRef.current) {
                    confirmResolveRef.current = Promise.resolve(
                        typeof options.confirmMessage === 'function'
                            ? options.confirmMessage()
                            : Dialog.alert(options.confirmMessage)
                    )
                        .then(applyNavigate)
                        .finally(() => (confirmResolveRef.current = null));
                }

                return;
            }

            return applyNavigate();
        },
        [routeNavigate]
    );

    return navigate;
}

/**
 * @description 提供增强型useNavigate和Navigate组件
 * 1. 增加sync和next参数，用于同步当前路由的所有search参数和指定要带上的next参数
 * 2. 增加confirmMessage参数，用于跳转前的消息提示
 * 3. 增加对外部链接的支持
 */
export const PowerNavigate: React.FC<PowerNavigateProps> = ({ to, ...options }) => {
    const navigate = usePowerNavigate();

    useEffect(() => {
        navigate(to, options);
    }, [navigate, to, options]);

    return null;
};
